// Types
type FhirIdentifierLike = {
  value?: string | null;
};

type FhirResourceLike = {
  id?: string | null;
  name?: string | null;
  identifier?: FhirIdentifierLike[] | null;
};

type FhirReferenceLike = {
  identifier?: {
    value?: string | null;
  } | null;
  reference?: string | null;
  display?: string | null;
};

// Normalization
function normalizeLookupValue(value?: string | null): string {
  return (value ?? "").trim();
}

// Matching helpers
function getValueTokens(value?: string | null): string[] {
  const normalizedValue = normalizeLookupValue(value);

  if (!normalizedValue) {
    return [];
  }

  const lowerValue = normalizedValue.toLowerCase();
  const segments = lowerValue
    .split(/[/|:#\-_\s]+/g)
    .map((segment) => segment.trim())
    .filter(Boolean);

  return Array.from(new Set([lowerValue, ...segments]));
}

function getReferenceCandidates(reference?: FhirReferenceLike | null): string[] {
  return [
    normalizeLookupValue(reference?.identifier?.value),
    normalizeLookupValue(reference?.reference?.split("/").pop()),
    normalizeLookupValue(reference?.display),
  ].filter(Boolean);
}

// Lookup helpers
export function resolveResourceIdByReference<T extends FhirResourceLike>(
  resources: T[],
  reference?: FhirReferenceLike | null
): string {
  const candidates = getReferenceCandidates(reference);

  if (candidates.length === 0) {
    return "";
  }

  const exactMatch = resources.find((resource) => {
    const values = [resource.id, resource.name, ...(resource.identifier?.map((identifier) => identifier.value) ?? [])]
      .map((value) => normalizeLookupValue(value).toLowerCase());
    return candidates.some((candidate) => values.includes(candidate.toLowerCase()));
  });

  if (exactMatch?.id) {
    return normalizeLookupValue(exactMatch.id);
  }

  const match = resources.find((resource) => {
    const resourceValues = [
      normalizeLookupValue(resource.id),
      normalizeLookupValue(resource.name),
      ...(resource.identifier?.map((identifier) =>
        normalizeLookupValue(identifier.value)
      ) ?? []),
    ].flatMap((value) => getValueTokens(value));

    return candidates.some((candidate) => {
      const candidateTokens = getValueTokens(candidate);

      return candidateTokens.some((candidateToken) =>
        resourceValues.some(
          (resourceToken) =>
            resourceToken === candidateToken ||
            resourceToken.endsWith(candidateToken) ||
            candidateToken.endsWith(resourceToken)
        )
      );
    });
  });

  return normalizeLookupValue(match?.id) || "";
}
